import { useRateFieldError } from "../../hooks/useRateFieldError";
import { FC } from "react";

import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";

import { isMin } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface SeatsFieldProps {
  tooltip?: string;
  unit?: string;
  "data-test"?: string;
}

export const SeatsField: FC<SeatsFieldProps> = ({ tooltip: tooltipProp, unit: unitProp, "data-test": dataTest }) => {
  const { t } = useTranslate();
  const { isRegistration, hasMultipleDates, isSweepstakes } = useRateContext();
  const { control } = useFormContext<StepTicketsFormData>();

  const fieldName = useRateFieldName("seats");
  const isGroup = Boolean(useWatch({ control, name: useRateFieldName("isGroup") }));
  const error = useRateFieldError("seats");

  const labelKey = hasMultipleDates ? "ticketing.seatsTotalAvailablePerDate" : "ticketing.seatsTotalAvailable";

  const unitKey = isGroup
    ? isRegistration
      ? "ticketing.seatsUnit.groupRegistrations"
      : "ticketing.seatsUnit.bundles"
    : isRegistration
      ? "ticketing.seatsUnit.registrations"
      : "ticketing.seatsUnit.tickets";

  const tooltipKey = isGroup
    ? isRegistration
      ? "ticketing.seatsInformations.groupRegistration"
      : "ticketing.seatsInformations.group"
    : isRegistration
      ? "ticketing.seatsInformations.registration"
      : "ticketing.seatsInformations";

  return (
    <DashboardTextField
      label={
        <span>
          <DashboardTypography variant="body2" sx={{ display: "inline", mr: 0.5 }}>
            {t("dashboard", labelKey)}
          </DashboardTypography>
          <DashboardInformationTooltip title={tooltipProp ?? t("dashboard", tooltipKey)} placement="top" alignOnText />
        </span>
      }
      name={fieldName}
      placeholder={t("dashboard", "ticketing.unlimited")}
      control={control}
      size="medium"
      endAdornment={
        <DashboardTypography variant="body2" vibe="text-neutral-quiet" sx={{ whiteSpace: "nowrap" }}>
          {unitProp ?? t("dashboard", unitKey)}
        </DashboardTypography>
      }
      errorMessage={error && t("dashboard", `${error.message}` as never)}
      rules={[isMin(0, isSweepstakes ? "sweepstakes.rates.seatsMinimum" : "ticketing.rateMinimumNumber")]}
      type="number"
      data-test={dataTest}
    />
  );
};
